import React, { FC } from 'react'
import { Trans, t } from '@lingui/macro'

import { SsoProviderEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import {
  IOrganisationWhitelistFoundDomains,
  OrganisationIPWhitelist
} from 'types/Organisation'
import { Box, Link, Checkbox } from 'front-end/src/components'
import Label from '../../../components/common/form/label/Label'
import { OrganisationCalendars } from '../../../graphql/public/PublicSchemaTypes'
import { getSSOProviders } from '../../../utils/SSOProviders'
import DomainLockConfig from '../authentication-tab/domain-lock-config/DomainLockConfig'
import { CalendarIntegrations } from './calendar-integrations/CalendarIntegrations'
import IpWhitelist from './ip-whitelist/IpWhitelist'
import Select from '../../../components/common/select/Select'
import { AccessibilityLabel } from '../../../components/common/AccessibilityLabel'
import { AccountTabSection } from '../TabContentSection'
import {
  useAccountAuthenticationTabQuery,
  useGetCalendarIntegrationsForOrganisationQuery
} from './AuthenticationTab.codegen'
import { useOrganisationPatchMutation } from '../AccountPage.codegen'

import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

import './AuthenticationTab.less'

export interface IAuthenticationTabForm {
  oauthConfigs: SsoProviderEnum[]
  domainLock: string[] | null
  allowSignup: boolean
  sessionExpiry: number
  ipWhitelist: OrganisationIPWhitelist
  domainWhitelistBreaches: IOrganisationWhitelistFoundDomains
  calendars: OrganisationCalendars
}
interface IProps {
  form: IAuthenticationTabForm
  onFormChange: (form: IAuthenticationTabForm) => Promise<void>
}
const availableSessionExpiryDays = [1, 2, 7, 14, 21, 28, 31, 62, 120, 365]
const availableSessionExpiryHours = [3, 8, 12]
const availableSessionExpiryMinutes = [5, 20, 60]
/**
 * all SAML ones-we only have metadata.xml file input in back-office admin dash, so any changes to this must be done by our customer success team or developers
 */
const SUPPORT_REQUIRED_PROVIDERS = [
  SsoProviderEnum.okta,
  SsoProviderEnum.googlesaml,
  SsoProviderEnum.azuresaml,
  SsoProviderEnum.fourth,
  SsoProviderEnum.rockrms,
  SsoProviderEnum.oak,
  SsoProviderEnum.keycloakveeam,
  SsoProviderEnum.pingidentity,
  SsoProviderEnum.azureb2c,
  SsoProviderEnum.auth0
]
export const AuthenticationTab: FC<IProps> = (props) => {
  const { form, onFormChange } = props
  const SSOProviders = React.useMemo(() => getSSOProviders(), [])
  const { data, refetch } = useAccountAuthenticationTabQuery()
  const { data: calendarsData, refetch: calendarsRefetch } =
    useGetCalendarIntegrationsForOrganisationQuery()
  const [patch] = useOrganisationPatchMutation()
  const { featureFlags } = useFeatureFlags()

  const calendars = calendarsData?.admin?.organisation?.calendars

  const handleChangeIpWhitelist = (
    ipWhitelist: OrganisationIPWhitelist
  ): Promise<void> => {
    const { onFormChange, form } = props
    return onFormChange({
      ...form,
      ipWhitelist
    })
  }
  const handleDomainWhitelistChange = (domainLock: string[]): void => {
    const { onFormChange, form } = props
    onFormChange({
      ...form,
      domainLock
    })
  }

  const renderOAuthSelects = () => {
    const { form, onFormChange } = props
    const { oauthConfigs } = form
    const providers = Object.keys(SSOProviders) as SsoProviderEnum[]
    return providers.map((providerName, index) => {
      const isSupportRequired =
        SUPPORT_REQUIRED_PROVIDERS.includes(providerName)
      const label = isSupportRequired ? (
        <span>
          {SSOProviders[providerName].title} (<Trans>contact</Trans>{' '}
          <Link href="mailto:support@looop.co">support@looop.co</Link>)
        </span>
      ) : (
        SSOProviders[providerName].title
      )
      return (
        <div key={index} className="AccountPage__checkBoxWrapper">
          <Checkbox
            id={`AuthenticationTab__oauth_${providerName}`}
            isDisabled={isSupportRequired}
            isChecked={
              form.oauthConfigs && form.oauthConfigs.includes(providerName)
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newSelection = event.target.checked
                ? [...oauthConfigs, providerName]
                : oauthConfigs.filter((p) => p !== providerName)
              onFormChange({ ...form, oauthConfigs: newSelection })
            }}
          >
            {label}
          </Checkbox>
        </div>
      )
    })
  }
  const sessionExpiryOpts = availableSessionExpiryMinutes
    .map((minutes) => {
      return {
        value: minutes * 60,
        label: t`${minutes} minutes`
      }
    })
    .concat(
      availableSessionExpiryHours.map((hours) => ({
        value: hours * 3600,
        label: t`${hours} hours`
      }))
    )
    .concat(
      availableSessionExpiryDays.map((days) => ({
        value: days * 3600 * 24,
        label: t`${days} days`
      }))
    )
    .reverse()
  const loginWay = data?.admin?.organisation?.features.account_name_login
    ? t`Email`
    : t`Account name`

  return (
    <AccountTabSection>
      <div className="AccountPage__box">
        <h4>
          <Trans>Login via OAuth</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Select an OAuth service below to allow your people to access Looop
            using existing credentials.
          </Trans>
        </p>
        <Label>
          <Trans>OAuth service</Trans>
        </Label>
        {renderOAuthSelects()}
      </div>
      {featureFlags?.calendars_integration && calendars && (
        <CalendarIntegrations
          calendars={calendars}
          calendarsRefetch={calendarsRefetch}
          msAuthUrl={
            calendarsData?.admin?.organisation
              ?.authUrlForMSGraphCalendarIntegration
          }
        />
      )}
      {
        // only display it for orgs that don't have account name login. Having these two flags as true doesn't make sense
        <div className="AccountPage__box">
          <h4>
            {loginWay} <Trans>login</Trans>
          </h4>
          <p className="text-description">
            <Trans>
              By checking this looop will hide the input form on the login
              screen and the API will not accept {loginWay}
              /password login from any client. Users must log in using one of
              the enabled SSO providers.
            </Trans>
          </p>
          <div className="AccountPage__checkBoxWrapper">
            <Checkbox
              isDisabled={
                data?.admin?.organisation?.only_sso_login === false &&
                form.oauthConfigs?.length === 0
              }
              isChecked={data?.admin?.organisation?.only_sso_login}
              onChange={async () => {
                await patch({
                  variables: {
                    input: {
                      only_sso_login: !data?.admin?.organisation?.only_sso_login
                    }
                  }
                })
                refetch()
              }}
            >
              <Trans>Disable {loginWay} login</Trans>
            </Checkbox>
          </div>
        </div>
      }
      <div className="AccountPage__box">
        <DomainLockConfig
          organisation={{
            domain_lock: form.domainLock,
            domainWhitelistBreaches: form.domainWhitelistBreaches
          }}
          onDomainWhitelistChange={handleDomainWhitelistChange}
        />
      </div>
      <div className="AccountPage__box">
        <h4>
          <Trans>Self sign up</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Self signup allows new people from within your organisation to add
            themselves up to your Looop account. New users must have an email
            account on a domain present in your domain allowlist.
          </Trans>
        </p>
        <div className="AccountPage__checkBoxWrapper">
          <Checkbox
            id="AuthenticationTab__email_comments"
            isChecked={form.allowSignup}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onFormChange({
                ...form,
                allowSignup: event.target.checked
              })
            }
          >
            <Trans>Allow self signup</Trans>
          </Checkbox>
        </div>
      </div>
      <div className="AccountPage__box">
        <h4>
          <Trans>Session expiry</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Force a user to sign back in after a period of inactivity.
          </Trans>
        </p>
        <div className="AuthenticationTab__expiry-select">
          <Trans>Sign in after</Trans>
          <AccessibilityLabel text={t`Session expiry`}>
            <Box m="0 8px 0">
              <Select
                options={sessionExpiryOpts}
                value={sessionExpiryOpts.find(
                  ({ value }) => form.sessionExpiry === value
                )}
                onChange={(opt) =>
                  onFormChange({
                    ...form,
                    // @ts-expect-error 9/11/2020
                    sessionExpiry: opt.value
                  })
                }
              />
            </Box>
          </AccessibilityLabel>
          <Trans>of inactivity</Trans>
        </div>
      </div>
      <div className="AccountPage__box">
        <h4>
          <Trans>IP allowlist</Trans>
        </h4>
        <p className="text-description">
          <Trans>
            Limit access to your Looop account to requests from within a
            specified IP range.
          </Trans>
        </p>
        <IpWhitelist
          whitelist={form.ipWhitelist}
          onUpdateWhitelist={handleChangeIpWhitelist}
        />
      </div>
    </AccountTabSection>
  )
}
